import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { changePassword } from '../services/accountApi';

/**
 * Full-page interstitial shown when `user.must_change_password` is true (e.g. an
 * onboarded customer who received a shared temporary password). Reuses the same
 * change-password endpoint as the profile modal: the customer enters their temp
 * password + a new one. On success the backend clears the flag, so a fresh
 * /users/me (via checkAuth) lets them through the ProtectedRoute gate.
 */
export const ForcedPasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const validate = (): string | null => {
    if (!current) return 'Current (temporary) password is required';
    if (!next) return 'New password is required';
    if (next.length < 12) return 'New password must be at least 12 characters';
    if (next === current) return 'New password must be different from the temporary one';
    if (next !== confirm) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setBusy(true);
    try {
      await changePassword(current, next);
      // Refresh the user so must_change_password is now false, then continue.
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (apiErr) {
      setError(apiErr instanceof Error ? apiErr.message : 'Failed to update password');
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F1F5F9', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#FFFFFF', borderRadius: 12, boxShadow: '0 10px 30px rgba(15,23,42,0.10)', padding: 28 }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 20, color: '#0F172A' }}>Set your password</h1>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: '#64748B' }}>
          {user?.email ? <>Welcome, {user.email}. </> : null}
          For security, please replace your temporary password before continuing.
        </p>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ marginBottom: 12, padding: 10, background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13 }}>
              {error}
            </div>
          )}

          <label style={fieldStyle}>
            <span style={labelStyle}>Temporary password</span>
            <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" style={inputStyle} />
          </label>
          <label style={fieldStyle}>
            <span style={labelStyle}>New password (min 12 chars)</span>
            <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={12} autoComplete="new-password" style={inputStyle} />
          </label>
          <label style={fieldStyle}>
            <span style={labelStyle}>Confirm new password</span>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" style={inputStyle} />
          </label>

          <button type="submit" disabled={busy} style={{ width: '100%', marginTop: 8, padding: 12, background: busy ? '#93C5FD' : '#2B7FFF', color: '#FFFFFF', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
            {busy ? 'Saving…' : 'Set Password & Continue'}
          </button>
        </form>

        <button
          onClick={() => { logout(); navigate('/login', { replace: true }); }}
          style={{ width: '100%', marginTop: 12, background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 13 }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
};

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 };
const labelStyle: React.CSSProperties = { fontSize: 13, color: '#475569', fontWeight: 500 };
const inputStyle: React.CSSProperties = { padding: '10px 12px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 14, outline: 'none' };

export default ForcedPasswordReset;
